/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { classifyTier, normalizeWebsiteUrl } from "@cosmos/airtable-data";

import { buildPartnerLinks, servicePartnerBadge } from "../cards/buildPartnerCardProps";

import type { ServicePartnerBadgeAssets } from "../cards/buildPartnerCardProps";
import type { ServicePartner, ServicePartnerCaseStudy } from "@cosmos/airtable-data";
import type { CosmosButtonProps, DirectoryCardBadge, DirectoryCardMetaItem } from "@cosmos/components";
import type { FeaturePanelsSectionFragment, ServicePartnerDetailFragment } from "@cosmos/hygraph-schema";

/** Subset of the `@epic-mw/i18n` messages dictionary this view consumes. */
type MessagesDict = Record<string, string>;

/** Detail-page labels authored on the directory's `servicePartnerDetail` chrome. */
export type ServicePartnerDetailLabels = Pick<
	ServicePartnerDetailFragment,
	| "backButtonLabel"
	| "caseStudiesLabel"
	| "industriesLabel"
	| "supportTypeLabel"
	| "ueSkillsLabel"
	| "languagesSupportedLabel"
	| "officeLocationsLabel"
>;

/** One attribute table row — a label and its (non-empty) multi-value list. */
export interface PartnerAttributeRow {
	label: string;
	values: string[];
}

/**
 * Shaped, render-ready data for the Service Partner detail page. Holds every
 * value derived from `partner` + Hygraph labels + localized messages so the orchestrator and its
 * subcomponents stay free of i18n lookups and Airtable-to-UI mapping.
 */
export interface ServicePartnerDetailView {
	badge: DirectoryCardBadge;
	backLabel: string;
	buttons: CosmosButtonProps[];
	meta: DirectoryCardMetaItem[];
	rows: PartnerAttributeRow[];
	caseStudySection: FeaturePanelsSectionFragment | null;
}

/**
 * Convert a partner's case studies into a single `FeaturePanelsSection`
 * fragment: one borderless (`disableCardBg`) card per case study, each card's
 * title/details/image/tags mapping to title/desc/asset/links, under a header
 * carrying only `heading`. Tags render as tag-styled chips via `linksAsTags`
 * (the FeaturePanel link list is the badge surface, not eyebrow badges). Card
 * ids are suffixed with the index so the two case studies stay distinct.
 * Returns null when there are no case studies so the caller can render nothing.
 */
const buildCaseStudySection = (
	caseStudies: ServicePartnerCaseStudy[],
	heading: string,
	slug: string,
): FeaturePanelsSectionFragment | null => {
	if (!caseStudies.length) return null;
	return {
		__typename: "CosmosSectionGameDevFeatureCard",
		id: `case-study-${slug}`,
		disableCardBg: true,
		config: {
			__typename: "CosmosConfigSection",
			heading: {
				__typename: "CosmosElementSectionHeading",
				title: { __typename: "RichText", html: heading, text: heading },
				buttons: [],
				modButtons: [],
			},
		},
		cards: caseStudies.map((caseStudy, index) => ({
			__typename: "CosmosElementGameDevFeatureCard",
			title: caseStudy.title ? { __typename: "RichText", html: caseStudy.title, text: caseStudy.title } : null,
			desc: caseStudy.details ?? null,
			eyebrowBadges: [],
			linksAsTags: true,
			links: (caseStudy.tags ?? []).map((label, i) => ({
				__typename: "CosmosElementLink",
				id: `case-study-${slug}-${index}-tag-${i}`,
				label,
				href: null,
			})),
			flip: Boolean(index % 2),
			buttons: [],
			modButtons: [],
			verticallyCenter: true,
			asset: caseStudy.imageUrl
				? {
						__typename: "Asset",
						id: `case-study-${slug}-${index}`,
						url: caseStudy.imageUrl,
						optUrl: caseStudy.imageUrl,
					}
				: null,
		})),
		buttons: [],
	};
};

/**
 * Build all render-ready data for the Service Partner detail page from the
 * Airtable row, the Hygraph-authored detail labels and localized messages.
 * Single source of truth for the page's label lookups and Airtable-to-UI shaping (badge, links/meta, attribute
 * rows, case studies) so it stays unit-testable without rendering — mirrors the
 * `buildPartnerCardProps` helpers the card adapters consume.
 */
export function buildServicePartnerDetailView(
	partner: ServicePartner,
	messages: MessagesDict,
	badgeAssets?: ServicePartnerBadgeAssets,
	labels?: ServicePartnerDetailLabels | null,
): ServicePartnerDetailView {
	// Badge mirrors the Service Partner card (tier tone + optional badge image).
	const badge = servicePartnerBadge(
		classifyTier(partner.badgeLevel),
		messages["epic.cosmos.directory.card.partner.service"] ?? "Service partner",
		badgeAssets,
	);

	// `meta` mirrors the Service Partner card's meta row — office locations +
	// languages — so the detail header reads consistently with the grid card.
	const { buttons, meta } = buildPartnerLinks(
		{ websiteUrl: normalizeWebsiteUrl(partner.website), email: partner.email, languages: partner.languages },
		{
			website: messages["epic.cosmos.directory.card.website"] ?? "Website",
			email: messages["epic.cosmos.directory.card.email"] ?? "Email",
		},
		{ officeLocation: partner.officeLocations.length > 0 ? partner.officeLocations.join(", ") : undefined },
	);

	// Attribute rows — only the non-empty multi-value columns render.
	const rows: PartnerAttributeRow[] = [
		{ label: labels?.industriesLabel ?? "Industries", values: partner.industries },
		{ label: labels?.supportTypeLabel ?? "Support type", values: partner.supportType },
		{ label: labels?.ueSkillsLabel ?? "UE skills", values: partner.ueSkills },
		{ label: labels?.languagesSupportedLabel ?? "Languages Supported", values: partner.languages },
		{ label: labels?.officeLocationsLabel ?? "Office locations", values: partner.officeLocations },
	].filter((row) => row.values.length > 0);

	const caseStudySection = buildCaseStudySection(
		partner.caseStudies,
		labels?.caseStudiesLabel ?? "Case studies",
		partner.slug,
	);

	return {
		badge,
		backLabel: labels?.backButtonLabel ?? "Back to directory",
		buttons,
		meta,
		rows,
		caseStudySection,
	};
}
